import type { Step, StepParam, StepValue } from '../model';
import { formatMoney, t, type Lang } from '../i18n';

/** "2026-05" → "5/2026"; "2026-04-01" → "1/4/2026" (the order Israeli payslips use). */
export const formatMonth = (ym: string) => `${Number(ym.slice(5, 7))}/${ym.slice(0, 4)}`;
export const formatDate = (iso: string) => `${Number(iso.slice(8, 10))}/${Number(iso.slice(5, 7))}/${iso.slice(0, 4)}`;

/**
 * A date with the month as a word, per language ("9 October 2026", "9 באוקטובר 2026"): where the day/month order would be ambiguous
 * ("9/10/2026" is 9 October for an Israeli and 10 September for an American).
 */
export const formatDateWords = (iso: string, lang: Lang): string => {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : lang, { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
};

/** A field or answer name: `detail.*` keys are already i18n keys; others are `field.<name>`. */
const fieldName = (lang: Lang, f: string) => t(lang, f.startsWith('detail.') ? f : `field.${f}`);

export function formatStepValue(lang: Lang, v: StepValue): string {
  if ('money' in v) return formatMoney(v.money, lang);
  if ('percent' in v) return `${v.percent}%`;
  if ('count' in v) return v.unit ? t(lang, `unit.${v.unit}`, { count: String(v.count) }) : String(v.count);
  if ('month' in v) return formatMonth(v.month);
  if ('date' in v) return formatDate(v.date);
  if ('text' in v) return t(lang, v.text);
  if ('fields' in v) return v.fields.length ? v.fields.map((f) => fieldName(lang, f)).join(', ') : t(lang, 'step.none');
  return v.months.length ? v.months.map(formatMonth).join(', ') : t(lang, 'step.none');
}

/** Where a figure comes from: the parameter's dates and whether it's confirmed. */
export function formatStepParam(lang: Lang, p: StepParam): string {
  const dates = p.to ? t(lang, 'step.param.range', { from: formatDate(p.from), to: formatDate(p.to) }) : t(lang, 'step.param.since', { from: formatDate(p.from) });
  return t(lang, p.confirmed ? 'step.param.confirmed' : 'step.param.unconfirmed', { dates });
}

/** One line of "how we calculated this": label, value, and where the figure comes from. */
export function formatStep(lang: Lang, s: Step): { label: string; value?: string; source?: string } {
  return {
    label: t(lang, s.label),
    ...(s.value ? { value: formatStepValue(lang, s.value) } : {}),
    ...(s.param ? { source: formatStepParam(lang, s.param) } : {}),
  };
}
